import React, { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback } from 'react';
import { DatabaseConnection, TableDescription } from '@/types';
import apiService from '@/services/api';
import { toast } from 'sonner';

interface TableScanStatus {
  total: number;
  scanned: number;
  synchronizing: number;
  failed: number;
  hasUnscanned: boolean;
  loading: boolean;
  lastUpdated?: Date;
}

interface DatabaseConnectionState {
  // Connection state
  connections: DatabaseConnection[];
  selectedConnectionId: string;
  connectionsLoading: boolean;
  connectionsError: string | null;
  connectionsLastFetch?: Date;

  // Table scan state
  tableDescriptions: { [connectionId: string]: TableDescription[] };
  tableScanStatus: { [connectionId: string]: TableScanStatus };
  scanningInProgress: { [connectionId: string]: boolean };

  // Request tracking
  pendingRequests: Set<string>;
}

interface DatabaseConnectionContextType extends DatabaseConnectionState {
  // Connection actions
  loadConnections: (force?: boolean) => Promise<void>;
  selectConnection: (connectionId: string) => void;
  createConnection: (connection: Omit<DatabaseConnection, 'id'>) => Promise<DatabaseConnection | null>;
  updateConnection: (id: string, connection: Partial<DatabaseConnection>) => Promise<void>;
  deleteConnection: (id: string) => Promise<void>;

  // Table scan actions
  getTableDescriptions: (connectionId: string, force?: boolean) => Promise<TableDescription[]>;
  getTableScanStatus: (connectionId: string) => TableScanStatus;
  triggerTableScan: (connectionId: string, tableNames?: string[]) => Promise<void>;
  refreshTableStatus: (connectionId: string) => Promise<void>;

  // Cache management
  clearCache: () => void;
  clearConnectionCache: (connectionId: string) => void;
}

const initialState: DatabaseConnectionState = {
  connections: [],
  selectedConnectionId: '',
  connectionsLoading: false,
  connectionsError: null,
  tableDescriptions: {},
  tableScanStatus: {},
  scanningInProgress: {},
  pendingRequests: new Set(),
};

const DatabaseConnectionContext = createContext<DatabaseConnectionContextType | undefined>(undefined);

export function DatabaseConnectionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<DatabaseConnectionState>(initialState);
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const pollingIntervalsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cancel all pending requests
      abortControllersRef.current.forEach(controller => controller.abort());
      abortControllersRef.current.clear();

      // Clear all polling intervals
      pollingIntervalsRef.current.forEach(interval => clearInterval(interval));
      pollingIntervalsRef.current.clear();
    };
  }, []);

  // Smart polling for table scan status
  const startTableScanPolling = useCallback((connectionId: string) => {
    // Stop existing polling
    const existingInterval = pollingIntervalsRef.current.get(connectionId);
    if (existingInterval) {
      clearInterval(existingInterval);
    }

    let pollCount = 0;
    const maxPolls = 100; // Maximum 5 minutes of polling at 3s intervals
    let lastRequestTime = 0;

    const poll = async () => {
      pollCount++;
      const now = Date.now();

      // Prevent overlapping requests
      if (now - lastRequestTime < 2000) return;
      lastRequestTime = now;

      try {
        const tables = await getTableDescriptions(connectionId, true);
        const hasUnscannedTables = tables.some(t => t.status !== 'SCANNED');

        if (!hasUnscannedTables || pollCount >= maxPolls) {
          // Stop polling when all tables are scanned or max attempts reached
          const interval = pollingIntervalsRef.current.get(connectionId);
          if (interval) {
            clearInterval(interval);
            pollingIntervalsRef.current.delete(connectionId);
          }

          setState(prev => ({
            ...prev,
            scanningInProgress: {
              ...prev.scanningInProgress,
              [connectionId]: false
            }
          }));

          if (!hasUnscannedTables) {
            toast.success('Database scan completed');
          }
        }
      } catch (error) {
        console.error('Polling error:', error);
        // Continue polling on error, but log it
      }
    };

    const interval = setInterval(poll, 3000);
    pollingIntervalsRef.current.set(connectionId, interval);
  }, []);

  const stopTableScanPolling = useCallback((connectionId: string) => {
    const interval = pollingIntervalsRef.current.get(connectionId);
    if (interval) {
      clearInterval(interval);
      pollingIntervalsRef.current.delete(connectionId);
    }
  }, []);

  // Request deduplication helper
  const makeRequest = useCallback(async <T,>(
    key: string,
    requestFn: (signal: AbortSignal) => Promise<T>
  ): Promise<T> => {
    // Check if request is already pending using setState to access current state
    let isPending = false;
    setState(prev => {
      isPending = prev.pendingRequests.has(key);
      return prev; // No state change
    });

    if (isPending) {
      throw new Error(`Request ${key} already in progress`);
    }

    // Cancel existing request if any
    const existingController = abortControllersRef.current.get(key);
    if (existingController) {
      existingController.abort();
    }

    // Create new abort controller
    const controller = new AbortController();
    abortControllersRef.current.set(key, controller);

    // Mark request as pending
    setState(prev => ({
      ...prev,
      pendingRequests: new Set([...prev.pendingRequests, key])
    }));

    try {
      const result = await requestFn(controller.signal);
      return result;
    } finally {
      // Clean up
      abortControllersRef.current.delete(key);
      setState(prev => ({
        ...prev,
        pendingRequests: new Set([...prev.pendingRequests].filter(k => k !== key))
      }));
    }
  }, []);

  const loadConnections = useCallback(async (force = false) => {
    const cacheKey = 'connections';

    // Use setState callback to access current state without dependencies
    let shouldSkip = false;
    setState(prev => {
      // Check cache validity (5 minutes)
      if (!force && prev.connections.length > 0 && prev.connectionsLastFetch) {
        const cacheAge = Date.now() - prev.connectionsLastFetch.getTime();
        if (cacheAge < 5 * 60 * 1000) {
          shouldSkip = true;
        }
      }
      return prev; // No state change
    });

    if (shouldSkip) {
      return; // Use cached data
    }

    try {
      const connections = await makeRequest(cacheKey, async (signal) => {
        setState(prev => ({ ...prev, connectionsLoading: true, connectionsError: null }));
        return await apiService.getDatabaseConnections();
      });

      setState(prev => ({
        ...prev,
        connections,
        connectionsLoading: false,
        connectionsLastFetch: new Date(),
        connectionsError: null
      }));
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('Failed to load connections:', error);
        setState(prev => ({
          ...prev,
          connectionsLoading: false,
          connectionsError: error.message || 'Failed to load connections'
        }));
        toast.error('Failed to load database connections');
      }
    }
  }, [makeRequest]);

  const selectConnection = useCallback((connectionId: string) => {
    setState(prev => ({ ...prev, selectedConnectionId: connectionId }));
    // Note: Preloading moved to useEffect to avoid forward reference
  }, []);

  const getTableDescriptions = useCallback(async (connectionId: string, force = false): Promise<TableDescription[]> => {
    const cacheKey = `tables-${connectionId}`;

    // Check cache validity (2 minutes for table data) - access current state via callback
    let cachedTables: TableDescription[] | undefined;
    let cachedStatus: TableScanStatus | undefined;

    setState(prev => {
      cachedTables = prev.tableDescriptions[connectionId];
      cachedStatus = prev.tableScanStatus[connectionId];
      return prev; // No state change, just reading current values
    });

    if (!force && cachedTables && cachedStatus && cachedStatus.lastUpdated) {
      const cacheAge = Date.now() - cachedStatus.lastUpdated.getTime();
      if (cacheAge < 2 * 60 * 1000) {
        return cachedTables; // Use cached data
      }
    }

    try {
      const tables = await makeRequest(cacheKey, async (signal) => {
        return await apiService.getTableDescriptions(connectionId);
      });

      // Calculate scan status
      const scannedTables = tables.filter(t => t.status === 'SCANNED');
      const synchronizingTables = tables.filter(t => t.status === 'SYNCHRONIZING');
      const failedTables = tables.filter(t => t.status === 'FAILED');

      const scanStatus: TableScanStatus = {
        total: tables.length,
        scanned: scannedTables.length,
        synchronizing: synchronizingTables.length,
        failed: failedTables.length,
        hasUnscanned: scannedTables.length < tables.length,
        loading: false,
        lastUpdated: new Date()
      };

      setState(prev => ({
        ...prev,
        tableDescriptions: {
          ...prev.tableDescriptions,
          [connectionId]: tables
        },
        tableScanStatus: {
          ...prev.tableScanStatus,
          [connectionId]: scanStatus
        }
      }));

      // Start polling if tables are still scanning
      if (scanStatus.hasUnscanned && scanStatus.synchronizing > 0) {
        setState(prev => ({
          ...prev,
          scanningInProgress: {
            ...prev.scanningInProgress,
            [connectionId]: true
          }
        }));
        startTableScanPolling(connectionId);
      } else {
        stopTableScanPolling(connectionId);
      }

      return tables;
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('Failed to load table descriptions:', error);
        setState(prev => ({
          ...prev,
          tableScanStatus: {
            ...prev.tableScanStatus,
            [connectionId]: {
              total: 0,
              scanned: 0,
              synchronizing: 0,
              failed: 0,
              hasUnscanned: false,
              loading: false,
              lastUpdated: new Date()
            }
          }
        }));
        toast.error('Failed to load table descriptions');
      }
      throw error;
    }
  }, [makeRequest, startTableScanPolling, stopTableScanPolling]); // Removed state objects from dependencies to prevent infinite loops

  const getTableScanStatus = useCallback((connectionId: string): TableScanStatus => {
    return state.tableScanStatus[connectionId] || {
      total: 0,
      scanned: 0,
      synchronizing: 0,
      failed: 0,
      hasUnscanned: false,
      loading: false
    };
  }, [state.tableScanStatus]);

  const triggerTableScan = useCallback(async (connectionId: string, tableNames?: string[]) => {
    try {
      setState(prev => ({
        ...prev,
        scanningInProgress: {
          ...prev.scanningInProgress,
          [connectionId]: true
        }
      }));

      await apiService.syncSchemas(connectionId, tableNames);

      // Start polling for scan progress
      startTableScanPolling(connectionId);

      toast.success('Table scan initiated');
    } catch (error: any) {
      console.error('Failed to trigger table scan:', error);
      setState(prev => ({
        ...prev,
        scanningInProgress: {
          ...prev.scanningInProgress,
          [connectionId]: false
        }
      }));
      toast.error('Failed to start table scan');
    }
  }, [startTableScanPolling]);

  const refreshTableStatus = useCallback(async (connectionId: string) => {
    await getTableDescriptions(connectionId, true);
  }, [getTableDescriptions]);

  const createConnection = useCallback(async (connection: Omit<DatabaseConnection, 'id'>): Promise<DatabaseConnection | null> => {
    try {
      const newConnection = await apiService.createDatabaseConnection(connection);
      setState(prev => ({
        ...prev,
        connections: [newConnection, ...prev.connections]
      }));
      toast.success('Database connection created');
      return newConnection;
    } catch (error: any) {
      console.error('Failed to create connection:', error);
      toast.error('Failed to create database connection');
      return null;
    }
  }, []);

  const updateConnection = useCallback(async (id: string, connection: Partial<DatabaseConnection>) => {
    try {
      const updated = await apiService.updateDatabaseConnection(id, connection);
      setState(prev => ({
        ...prev,
        connections: prev.connections.map(conn => conn.id === id ? updated : conn)
      }));
      toast.success('Database connection updated');
    } catch (error: any) {
      console.error('Failed to update connection:', error);
      toast.error('Failed to update database connection');
    }
  }, []);

  const deleteConnection = useCallback(async (id: string) => {
    try {
      await apiService.deleteDatabaseConnection(id);
      setState(prev => ({
        ...prev,
        connections: prev.connections.filter(conn => conn.id !== id),
        tableDescriptions: { ...prev.tableDescriptions, [id]: undefined } as any,
        tableScanStatus: { ...prev.tableScanStatus, [id]: undefined } as any,
        scanningInProgress: { ...prev.scanningInProgress, [id]: undefined } as any,
        selectedConnectionId: prev.selectedConnectionId === id ? '' : prev.selectedConnectionId
      }));

      // Stop polling for this connection
      stopTableScanPolling(id);

      toast.success('Database connection deleted');
    } catch (error: any) {
      console.error('Failed to delete connection:', error);
      toast.error('Failed to delete database connection');
    }
  }, [stopTableScanPolling]);

  const clearCache = useCallback(() => {
    setState(prev => ({
      ...prev,
      tableDescriptions: {},
      tableScanStatus: {},
      connectionsLastFetch: undefined
    }));
  }, []);

  const clearConnectionCache = useCallback((connectionId: string) => {
    setState(prev => ({
      ...prev,
      tableDescriptions: { ...prev.tableDescriptions, [connectionId]: undefined } as any,
      tableScanStatus: { ...prev.tableScanStatus, [connectionId]: undefined } as any
    }));
  }, []);

  // Load connections on mount
  useEffect(() => {
    loadConnections();
  }, []); // Empty dependency array - only run once on mount

  // Preload table descriptions when connection is selected
  useEffect(() => {
    if (state.selectedConnectionId) {
      getTableDescriptions(state.selectedConnectionId);
    }
  }, [state.selectedConnectionId, getTableDescriptions]);

  const value: DatabaseConnectionContextType = React.useMemo(() => ({
    ...state,
    loadConnections,
    selectConnection,
    createConnection,
    updateConnection,
    deleteConnection,
    getTableDescriptions,
    getTableScanStatus,
    triggerTableScan,
    refreshTableStatus,
    clearCache,
    clearConnectionCache,
  }), [
    state,
    loadConnections,
    selectConnection,
    createConnection,
    updateConnection,
    deleteConnection,
    getTableDescriptions,
    getTableScanStatus,
    triggerTableScan,
    refreshTableStatus,
    clearCache,
    clearConnectionCache,
  ]);

  return (
    <DatabaseConnectionContext.Provider value={value}>
      {children}
    </DatabaseConnectionContext.Provider>
  );
}

export function useDatabaseConnection() {
  const context = useContext(DatabaseConnectionContext);
  if (context === undefined) {
    throw new Error('useDatabaseConnection must be used within a DatabaseConnectionProvider');
  }
  return context;
}